import { useCallback } from "react";
import type { EditorEvents } from "@tiptap/core";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import {
  createTipTapFromText,
  isPlainText,
  isRichTextContent,
} from "../../utils/richTextHelpers";
import { useTextBlockUpdate } from "../../utils/useTextBlockUpdate";
import type { TextRendererProps } from "./TextRenderer.types";

export const useTextBlockInteraction = ({
  blockPath,
  text,
}: {
  blockPath: string;
  text: TextRendererProps["text"];
}) => {
  const { shouldAllowInlineEditing, updateTextProp } =
    useTextBlockUpdate(blockPath);
  const startInlineEditing = useBuilderStore(state => state.startInlineEditing);
  const stopInlineEditing = useBuilderStore(state => state.stopInlineEditing);

  const handleDoubleClick = useCallback(() => {
    if (!shouldAllowInlineEditing) {
      return;
    }
    if (isPlainText(text)) {
      const tipTapContent = createTipTapFromText(text);
      updateTextProp(tipTapContent, "rich-text");
    }
    startInlineEditing(blockPath);
  }, [
    shouldAllowInlineEditing,
    text,
    updateTextProp,
    startInlineEditing,
    blockPath,
  ]);

  const handleBlur = useCallback(
    (props: EditorEvents["blur"]) => {
      const { editor } = props;
      stopInlineEditing();
      if (editor) {
        const content = editor.getJSON();
        if (isRichTextContent(content)) {
          updateTextProp(content, "rich-text");
        }
      }
    },
    [stopInlineEditing, updateTextProp],
  );

  return {
    handleDoubleClick,
    handleBlur,
  };
};
